import type { WrapperFC } from '@george-labs.com/design-system';
import { useRef } from 'react';
import { OverviewCardMenu } from '@george-labs.com/design-system';

import type { Account } from '@shared/products-types';
import type { CopyProductNumberAction } from '@retail/overview-utils';
import type { ProductWithHelpers } from '@shared/products-core';
import { useNotifications } from '@shared/snackbar';
import { ActiveProductProvider, useActiveCustomizationFeature } from '@shared/products-core';

import type { DropdownEntry } from '../../overview-actions-dropdown/types';
import { ProductImageUpload as PartialPAPIImageChange } from '../ProductImageUpload';
import { AliasChange as PartialPAPIAliasChange } from '../personalization-card/customizations/AliasChange';
import { ColorChange as PartialPAPIColorChange } from '../personalization-card/customizations/ColorChange';
import { CardMenuDropdownToggle } from '../../overview-actions-dropdown/CardMenuDropdownToggle';
import { useDropdownEntries } from '../../overview-actions-dropdown/useDropdownEntries';
import {
    getDropdownActions,
    getRegisteredDropdownActions,
} from '../../overview-actions-dropdown/ActionsDropdownService';

interface Props {
    useRegisteredItems?: boolean;
    isAliasChangeAllowed?: boolean;
    copyProductNumberAction?: CopyProductNumberAction;
    settingsUrl?: string;
    account: ProductWithHelpers<Account>;
}

export const ActionsDropdown: WrapperFC<Props> = ({
    useRegisteredItems = false,
    isAliasChangeAllowed = true,
    copyProductNumberAction,
    settingsUrl,
    account,
}) => {
    const { displaySuccess } = useNotifications('overview-dropdown-actions');
    const {
        isAliasChangeCustomizationActive,
        isColorChangeCustomizationActive,
        activateAliasChangeCustomization,
        activateColorChangeCustomization,
    } = useActiveCustomizationFeature();
    const imageUploadInputRef = useRef<HTMLInputElement>(null);

    const dropdownActions = useRegisteredItems
        ? getRegisteredDropdownActions(
              account,
              isAliasChangeAllowed,
              copyProductNumberAction,
              settingsUrl,
          )
        : getDropdownActions(account, isAliasChangeAllowed);

    const { dropdownEntries, performEntryAction } = useDropdownEntries(
        dropdownActions,
        displaySuccess,
    );

    if (!dropdownEntries.length) {
        return null;
    }

    if (isColorChangeCustomizationActive) {
        return (
            <ActiveProductProvider value={account}>
                <PartialPAPIColorChange
                    toggle={<CardMenuDropdownToggle data-cy="color-change-toggle-dropdown" />}
                />
            </ActiveProductProvider>
        );
    }

    if (isAliasChangeCustomizationActive) {
        return (
            <ActiveProductProvider value={account}>
                <PartialPAPIAliasChange
                    toggle={<CardMenuDropdownToggle data-cy="name-change-toggle-dropdown" />}
                    trigger="overview"
                />
            </ActiveProductProvider>
        );
    }
    const onSelect = (action: DropdownEntry) => {
        if (action.value === 'CHANGE_IMAGE') {
            imageUploadInputRef.current?.click();
            return;
        }

        if (action.value === 'CHANGE_COLOR') {
            activateColorChangeCustomization();
            return;
        }

        if (action.value === 'CHANGE_NAME') {
            activateAliasChangeCustomization();
            return;
        }

        performEntryAction(action.value);
    };

    const renderToggle = () => (
        <CardMenuDropdownToggle data-cy="overview-card-secondary-menu-button" />
    );

    return (
        <>
            <OverviewCardMenu items={dropdownEntries} onSelect={onSelect} toggle={renderToggle} />
            <ActiveProductProvider value={account}>
                <PartialPAPIImageChange
                    ref={imageUploadInputRef}
                    imageChangeContext="overview"
                    trigger="overview"
                />
            </ActiveProductProvider>
        </>
    );
};

ActionsDropdown.wrapsComponent = OverviewCardMenu;
